import { Link, generatePath } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { ContactListFields } from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  companyMsg,
  emailMsg,
  eoeInUseColumnMsg,
  eoeInUseFalseMsg,
  eoeInUseTrueMsg,
  nameMsg,
  noUsersMsg,
  phoneNumberMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { Highlight } from '../Highlight/Highlight.js';
import { HighlightFormattedPhoneNumber } from '../Highlight/HighlightFormattedPhoneNumber.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  Table,
} from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type { ContactSearchResponse } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { JSX } from 'react';

export interface ContactsListProps {
  contacts: ContactSearchResponse[];
  total: number;
  searchInput?: string;
  authenticatedUser?: AuthenticatedUser;
}

const contactsListColumns: CLTableColumn[] = [
  {
    key: ContactListFields.CONTACT_NAME,
    label: t.PNS1(nameMsg),
    sortable: true,
    sorted: 'asc',
  },
  {
    key: ContactListFields.CONTACT_PHONE_NUMBER,
    label: t.AGIJ(phoneNumberMsg),
    sortable: true,
  },
  {
    key: ContactListFields.CONTACT_EMAIL,
    label: t.OKYY(emailMsg),
    sortable: true,
  },
  {
    key: ContactListFields.CONTACT_EOE_IN_USE,
    label: t.S0FN(eoeInUseColumnMsg),
    sortable: true,
  },
  {
    key: ContactListFields.COMPANY,
    label: t.KJTS(companyMsg),
    sortable: false,
  },
];

const getContactRows = (
  contacts: ContactSearchResponse[],
  searchInput?: string,
  authenticatedUser?: AuthenticatedUser
) => {
  const searchTerms = searchInput?.split(' ') || [];
  return contacts?.map(contact => {
    const name = `${contact.result.firstName || ''} ${contact.result.lastName || ''}`;
    return {
      [ContactListFields.CONTACT_NAME]: (
        <Link
          to={`${generatePath(paths.COMPANY_INFO_CONTACT, { contactMasterId: contact.result.contactMasterId || '' })}?mdmId=${contact.result.accountMasterId}`}
        >
          <Highlight text={name} highlights={searchTerms} />
        </Link>
      ),
      [ContactListFields.CONTACT_PHONE_NUMBER]: (
        <HighlightFormattedPhoneNumber
          formattedPhoneNumber={formatPhoneNumber(contact.result.phoneNumber || '', true)}
          search={searchInput}
        />
      ),
      [ContactListFields.CONTACT_EMAIL]: <Highlight text={contact.result.email || ''} highlights={searchTerms} />,
      [ContactListFields.CONTACT_EOE_IN_USE]: contact.result.eoeInUse
        ? t.V34H(eoeInUseTrueMsg)
        : t.MUF5(eoeInUseFalseMsg),
      [ContactListFields.COMPANY]: getCompanyName(authenticatedUser, contact.result.accountMasterId),
    };
  });
};

export const ContactsList = ({ contacts, total, searchInput, authenticatedUser }: ContactsListProps): JSX.Element => {
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const rows = getContactRows(contacts, searchInput, authenticatedUser);

  return (
    <div className="of-contacts-list">
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listElement={<Table columns={contactsListColumns} noItemsText={t.UMEE(noUsersMsg)} rows={rows || []} />}
        listSearch={
          <ListSearch
            onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
          />
        }
        listPagination={<ListPagination totalItems={total} />}
      />
    </div>
  );
};
